import React, { useCallback, useEffect, useState } from 'react';

import { Alert, Button, Input, List, Table } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import {
  LaunchAccessSwitch,
  LoadingScreen,
  fetchLaunchAccessRoster,
} from 'components';
import type { LaunchAccessAnswer } from 'components';
import { useModal } from 'services';
import EditClassModal from './EditClassModal';
import UpdatePasswordModal from './UpdatePasswordModal';
import { AdminTrainerOverview, fetchAdminTrainerOverview } from './api';

const errorMessage = (error: unknown) =>
  error instanceof Error
    ? error.message
    : 'The trainer list could not be loaded.';

type SearchableHeaderCellProps =
  React.ThHTMLAttributes<HTMLTableCellElement> & {
    search?: {
      label: string;
      value: string;
      onChange: (value: string) => void;
    };
  };

// Render inputs only in the real header, not in the table's hidden sizing row.
const SearchableHeaderCell: React.FC<SearchableHeaderCellProps> = ({
  search,
  children,
  ...cellProps
}) => (
  <th {...cellProps}>
    {children}
    {search && (
      <div style={{ marginTop: 6, maxWidth: 320 }}>
        <Input
          size="small"
          aria-label={search.label}
          placeholder={search.label}
          prefix={<SearchOutlined />}
          allowClear
          value={search.value}
          onChange={event => search.onChange(event.target.value)}
        />
      </div>
    )}
  </th>
);

const tableComponents = { header: { cell: SearchableHeaderCell } };
const textColumnStyle: React.CSSProperties = {
  whiteSpace: 'normal',
  overflowWrap: 'anywhere',
};

const Trainers: React.FC = () => {
  const [trainers, setTrainers] = useState<AdminTrainerOverview[]>([]);
  const [usernameSearch, setUsernameSearch] = useState('');
  const [classSearch, setClassSearch] = useState('');
  const [waiting, setWaiting] = useState(true);
  const [error, setError] = useState<string>();
  const [reloadVersion, setReloadVersion] = useState(0);
  // Who may launch a scenario into their class. `undefined` means the switch
  // is not served by this deployment yet, and the column is then not drawn at
  // all rather than shown as a dead control.
  const [launchAccess, setLaunchAccess] = useState<
    Record<number, LaunchAccessAnswer> | undefined
  >(undefined);

  const removeClass = useCallback((klassId: number) => {
    setTrainers(current =>
      current.map(trainer => ({
        ...trainer,
        klasses: trainer.klasses.filter(klass => klass.id !== klassId),
      })),
    );
  }, []);

  const [PasswordModal, showPassword] = useModal(UpdatePasswordModal);
  const [ClassModal, showClassModal] = useModal(EditClassModal);

  useEffect(() => {
    let active = true;
    setWaiting(true);
    setError(undefined);

    fetchAdminTrainerOverview()
      .then(result => {
        if (active) setTrainers(result);
      })
      .catch(requestError => {
        if (active) setError(errorMessage(requestError));
      })
      .finally(() => {
        if (active) setWaiting(false);
      });

    return () => {
      active = false;
    };
  }, [reloadVersion]);

  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const rows = await fetchLaunchAccessRoster();
        if (!active || !rows) return;
        const byId: Record<number, LaunchAccessAnswer> = {};
        rows.forEach(row => {
          if (row.user_id != null) byId[row.user_id] = row;
        });
        setLaunchAccess(byId);
      } catch {
        // The trainer list must render whatever the switch route answers.
        if (active) setLaunchAccess(undefined);
      }
    })();
    return () => {
      active = false;
    };
  }, [reloadVersion]);

  if (waiting) return <LoadingScreen />;

  if (error) {
    return (
      <Alert
        type="error"
        showIcon
        message={error}
        action={
          <Button size="small" onClick={() => setReloadVersion(v => v + 1)}>
            Retry
          </Button>
        }
      />
    );
  }

  const usernameQuery = usernameSearch.trim().toLowerCase();
  const classQuery = classSearch.trim().toLowerCase();
  const filteredTrainers = trainers.filter(
    trainer =>
      trainer.username.toLowerCase().includes(usernameQuery) &&
      (!classQuery ||
        trainer.klasses.some(klass =>
          klass.name.toLowerCase().includes(classQuery),
        )),
  );

  return (
    <>
      <Table
        rowKey="id"
        sticky={{ offsetHeader: 64 }}
        tableLayout="fixed"
        scroll={{ x: 960 }}
        // Let the header stick to the page below the 64px navigation bar.
        style={{ overflow: 'visible' }}
        components={tableComponents}
        dataSource={filteredTrainers}
        columns={[
          { title: 'ID', dataIndex: 'id', width: 64 },
          {
            title: 'Username',
            dataIndex: 'username',
            // The two text columns share the space left by the fixed columns.
            onCell: () => ({ style: textColumnStyle }),
            onHeaderCell: (): SearchableHeaderCellProps => ({
              style: textColumnStyle,
              search: {
                label: 'Search usernames',
                value: usernameSearch,
                onChange: setUsernameSearch,
              },
            }),
          },
          {
            title: 'Classes (press class title to manage class users)',
            dataIndex: 'klasses',
            onCell: () => ({ style: textColumnStyle }),
            onHeaderCell: (): SearchableHeaderCellProps => ({
              style: textColumnStyle,
              search: {
                label: 'Search classes',
                value: classSearch,
                onChange: setClassSearch,
              },
            }),
            render(_text, record: AdminTrainerOverview) {
              const visibleClasses = record.klasses.filter(klass =>
                klass.name.toLowerCase().includes(classQuery),
              );
              return (
                <List>
                  {visibleClasses.map(klass => (
                    <List.Item
                      key={klass.id}
                      style={{ padding: 2 }}
                      onClick={() =>
                        showClassModal({
                          klassId: klass.id,
                          onDeleted: removeClass,
                        })
                      }
                    >
                      {klass.name}
                    </List.Item>
                  ))}
                </List>
              );
            },
          },
          ...(launchAccess
            ? [
                {
                  title: 'Launch from Scenario',
                  width: 170,
                  render(_text: unknown, record: AdminTrainerOverview) {
                    const row = launchAccess[record.id];
                    if (!row) return null;
                    return (
                      <LaunchAccessSwitch
                        row={{ ...row, username: record.username }}
                        onChanged={updated =>
                          setLaunchAccess(current => ({
                            ...(current || {}),
                            [record.id]: updated,
                          }))
                        }
                      />
                    );
                  },
                },
              ]
            : []),
          {
            title: 'Actions',
            width: 130,
            render(_text, record: AdminTrainerOverview) {
              return (
                <Button onClick={() => showPassword({ trainer: record })}>
                  Set password
                </Button>
              );
            },
          },
        ]}
        pagination={false}
        bordered
      />

      {PasswordModal}
      {ClassModal}
    </>
  );
};

export default Trainers;
